import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Editor, Path, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import { Box } from '@mui/material';
import CustomTypography from '../../../../components/mui/CustomTypography';
import playIcon from '../../../../assets/images/play.svg';
import _ from 'lodash';
import SlateSpeakerLabel from './slateSpeakerLabel';
import { useDispatch, useSelector } from 'react-redux';
import { updateCompletedPercentage, updateSlateIsDirty } from '../actions/slateDataActions';
import { useParams } from 'react-router-dom';
import SlateTimeLabel from './slateTimeLabel';
import { BpCheckbox } from '../../../../components/mui/BpCheckbox';
import { trackEvent } from '../../../../utils/ga';
import ExaminationForm from './ExaminationForm';

// Separate the PlayButton component for better performance
const PlayButton = React.memo(({ fontSize, onClick }) => (
  <img
    src={playIcon}
    className="image-fluid"
    alt="play icon"
    contentEditable={false}
    onClick={onClick}
    style={{
      width: `${Math.max(fontSize, 10)}px`,
      height: `${Math.max(fontSize, 10)}px`,
      cursor: 'pointer',
      userSelect: 'none',
    }}
  />
));

const DefaultElement = React.memo(({ attributes, children, element, editor }) => {
  const path = ReactEditor.findPath(editor, element);
  const index = path[0];
  return (
    <p id={`paragraph-${index}`} {...attributes}>
      {children}
    </p>
  );
});

const SpeakerElement = React.memo(({ attributes, children, element, editor }) => {
  const { id } = useParams();
  const path = ReactEditor.findPath(editor, element);
  const index = path[0];
  const dispatch = useDispatch();
  const { fontSize } = useSelector((state) => state.settings);
  const { selectedNode, data } = useSelector((state) => state.slateData);

  // FIXED: Always initialize checkbox as unchecked regardless of element.completed
  const [isChecked, setIsChecked] = useState(false);

  const [startTime, setStartTime] = useState(
    element?.children[0]?.data?.manualStart || element?.children[0]?.data?.start || 0,
  );
  const legalTemplateFormFieldDetails = useSelector((state) => state?.settings?.legalTemplateFormFieldDetails);
  const examinations = legalTemplateFormFieldDetails?.FormField?.Examinations || [];

  let editorDetails = useSelector((state) => state?.editorDetails?.user?.data);

  const parsedEditorDetails = editorDetails ? JSON.parse(editorDetails?.value || '') : null;
  const allowEdit = parsedEditorDetails?.Status !== 'Rework'

  // REMOVED: The useEffect that was setting isChecked based on element.completed

  // Memoize handlers to prevent unnecessary re-renders
  const handlePlayIconClick = useCallback(() => {
    const audioElement = document.getElementById(`audiofile_${id}`);
    if (audioElement) {
      audioElement.currentTime = element.children[0].data.start;
      audioElement.play();
    }
  }, [element.children[0]?.data?.start, id]);

  const handleSpeakerChange = useCallback(
    (newSpeaker) => {
      const path = ReactEditor.findPath(editor, element);
      trackEvent('speaker_changed', {
        speaker_changed: `${element.speaker} --> ${newSpeaker}`,
      });
      Transforms.setNodes(editor, { speaker: newSpeaker }, { at: path });
    },
    [editor, element],
  );

  const handleCheckboxChange = useCallback(
    (event) => {
      if (!allowEdit) return;
      const newCheckedState = event.target.checked;
      setIsChecked(newCheckedState);

      const path = ReactEditor.findPath(editor, element);
      Transforms.setNodes(editor, { completed: newCheckedState }, { at: path });

      dispatch(
        updateCompletedPercentage({
          id: element.id,
          completed: newCheckedState,
        }),
      );
      dispatch(updateSlateIsDirty(true));
    },
    [editor, element.id, dispatch],
  );

  const updateSpeaker = useCallback(
    (oldSpeaker, newSpeaker, onComplete) => {
      if (!oldSpeaker || !newSpeaker) return;

      Editor.withoutNormalizing(editor, () => {
        const speakerNodes = new Map();
        Array.from(
          Editor.nodes(editor, {
            at: [],
            match: (n, path) => {
              if (path.length === 0) return false;
              try {
                const parentPath = Path.parent(path);
                if (Editor.hasPath(editor, parentPath)) {
                  const [parent] = Editor.node(editor, parentPath);
                  const isElement = (node) => node && typeof node === 'object' && 'type' in node;

                  if (isElement(parent) && parent.type === 'speaker' && parent.speaker === oldSpeaker) {
                    const pathKey = parentPath.toString();
                    if (!speakerNodes.has(pathKey)) {
                      speakerNodes.set(pathKey, parentPath);
                    }
                    return true;
                  }
                }
              } catch (error) {
                console.error('Path error:', error);
              }

              return false;
            },
          }),
        );

        Array.from(speakerNodes.values()).forEach((path) => {
          try {
            if (Editor.hasPath(editor, path)) {
              Transforms.setNodes(editor, { speaker: newSpeaker }, { at: path });
            }
          } catch (error) {
            console.error('Transform error:', error);
          }
        });
      });

      trackEvent('speaker_renamed', {
        speaker_changed: `${oldSpeaker} --> ${newSpeaker}`,
      });

      if (onComplete) onComplete();
    },
    [editor],
  );

  const handleTimeUpdate = useCallback(
    (newTime) => {
      const path = ReactEditor.findPath(editor, element);
      const newChild = {
        ...element.children[0],
        data: {
          ...element.children[0].data,
          manualStart: newTime,
        },
      };
      const childPath = [...path, 0];
      Transforms.setNodes(editor, newChild, { at: childPath });
      setStartTime(newTime);
    },
    [editor, element],
  );

  // Memoize styles for better performance
  const containerStyles = useMemo(
    () => ({
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    }),
    [],
  );

  const contentStyles = useMemo(
    () => ({
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 2,
      borderBottom: '1px solid rgb(235, 235, 235)',
      pr: '25px',
    }),
    [],
  );

  const textWrapperStyles = useMemo(
    () => ({
      fontSize: `${Math.max(fontSize, 11)}px !important`,
      color: '#190041 !important',
      lineHeight: 1.8,
      paddingRight: '20px',
      textAlign: 'left',
      outline: 'none',
    }),
    [fontSize],
  );

  return (
    <Box
      id={`element-${index}`}
      sx={containerStyles}
      data-audio-paragraph-id={element.id}
      data-start={startTime.toString()}
      {...attributes}
    >
      <Box sx={contentStyles}>
        <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', py: '20px', gap: 1 }}>
          <Box contentEditable={false} sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {element.type !== 'paragraph' && (
              <SlateSpeakerLabel element={element} onChange={handleSpeakerChange} onUpdate={updateSpeaker} allowEdit={allowEdit} />
            )}
            <PlayButton fontSize={fontSize} onClick={handlePlayIconClick} />
            <SlateTimeLabel fontSize={fontSize} startTime={startTime} onChange={handleTimeUpdate} allowEdit={allowEdit} />
            {examinations?.length > 0 && (
              <ExaminationForm element={element} editor={editor} fontSize={fontSize} examinations={examinations} allowEdit={allowEdit} />
            )}
          </Box>

          <CustomTypography
            color={'#190041'}
            size={'smallMedium'}
            variant="span"
            contentEditable={true}
            suppressContentEditableWarning={true}
            className="slate-words-wrapper"
            id={`paragraph-${index}`}
            sx={textWrapperStyles}
            {
              ...(!allowEdit ? {
                contentEditable: false,
              } : {})
            }
          >
            {children}
          </CustomTypography>
        </Box>

        <BpCheckbox
          contentEditable={false}
          checked={element.completed}
          onChange={handleCheckboxChange}
          isFocused={index === selectedNode}
        />
      </Box>
    </Box>
  );
});

const SlateElement = ({ attributes, children, element, editor }) => {
  // Use early return pattern for better performance
  if (element.type === 'speaker' || element.type === 'paragraph') {
    return (
      <SpeakerElement attributes={attributes} element={element} editor={editor}>
        {children}
      </SpeakerElement>
    );
  }

  return (
    <DefaultElement attributes={attributes} element={element} editor={editor}>
      {children}
    </DefaultElement>
  );
};

// Optimize the memo comparison function
const memoCompare = (prevProps, nextProps) => {
  // Fast-path: Different children always causes re-render
  if (prevProps.children !== nextProps.children) return false;

  // Fast-path: Different types always causes re-render
  if (prevProps.element.type !== nextProps.element.type) return false;

  const prevEl = prevProps.element;
  const nextEl = nextProps.element;

  // Specific comparison for speaker elements
  if (prevEl.type === 'speaker') {
    return (
      prevEl.speaker === nextEl.speaker &&
      prevEl.completed === nextEl.completed &&
      prevEl.children[0]?.data?.start === nextEl.children[0]?.data?.start
    );
  }

  // Specific comparison for paragraph elements
  if (prevEl.type === 'paragraph') {
    return prevEl.completed === nextEl.completed;
  }

  // Default comparison for other elements
  return _.isEqual(prevEl, nextEl);
};

SlateElement.displayName = 'SlateElement';

export default React.memo(SlateElement, memoCompare);
